// Network lists and a task group's egress (D81).
export default {
  lists: {
    label: "Network lists",
    create: "New list",
    empty: "No network lists yet",
    emptyBody:
      "A network list names the domains your agents' containers may reach, on top of the platform essentials. Create one, then choose it for a task group.",
    loadFailed: "Could not load your network lists",
    columns: { name: "List", domains: "Domains", usedBy: "Used by", updated: "Updated" },
    domainCount: (n: number) => `${n} ${n === 1 ? "domain" : "domains"}`,
    groupCount: (n: number) => (n === 0 ? "No task group" : `${n} ${n === 1 ? "task group" : "task groups"}`),
  },
  create: {
    title: "New network list",
    name: "Name",
    namePlaceholder: "Backend services",
    description: "Description",
    descriptionPlaceholder: "What the agents need to reach, and why",
    domains: "Domains (optional)",
    domainsHint: "One per line, or separated by commas. example.com is that host; .example.com or *.example.com is it and every subdomain.",
    create: "Create list",
    cancel: "Cancel",
    created: (name: string) => `Created ${name}`,
    invalid: (entries: string) => `Not domains: ${entries}`,
  },
  essentials: {
    title: "Platform essentials",
    hint: "Always allowed, whatever list a group uses: the agent needs them to reach its model, the broker and the code hosts.",
    loadFailed: "Could not load the platform essentials",
  },
  list: {
    loadFailed: "Could not load this list",
    details: "Details",
    name: "Name",
    description: "Description",
    save: "Save",
    saved: "Saved",
    domains: "Domains",
    domainsHint: "Changes reach running agents within seconds.",
    search: "Search domains",
    noDomains: "No domains yet. Add some, or upload a file.",
    noMatch: "No domain matches your search.",
    add: "Add",
    addLabel: "Domains to add",
    addPlaceholder: "pypi.org, .pythonhosted.org",
    added: (n: number) => `Added ${n} ${n === 1 ? "domain" : "domains"}`,
    remove: "Remove",
    removeLabel: (domain: string) => `Remove ${domain}`,
    removeSelected: (n: number) => `Remove ${n} selected`,
    removed: (n: number) => `Removed ${n} ${n === 1 ? "domain" : "domains"}`,
    selectAll: "Select every domain shown",
    select: (domain: string) => `Select ${domain}`,
    upload: "Upload a file",
    uploadHint: "A .txt with one domain per line, or a .csv (its first column).",
    uploaded: (added: number, duplicates: number) => `Added ${added}; ${duplicates} already in the list`,
    uploadInvalid: (n: number, sample: string) => `${n} ${n === 1 ? "line is" : "lines are"} not a domain: ${sample}`,
    usedBy: "Used by",
    usedByHint: "Every task group here gets this list's changes.",
    notUsed: "No task group uses this list yet. Choose it when you create or edit a group.",
    delete: "Delete list",
    deleteTitle: (name: string) => `Delete ${name}?`,
    deleteBody: "The list goes for good. A list a task group uses cannot be deleted: choose another list for those groups first.",
    deleted: (name: string) => `Deleted ${name}`,
  },
  group: {
    title: "Network",
    description: "What the agents reached, and what was blocked",
    open: "Open network details",
    allowed: "Allowed",
    blocked: "Blocked",
    requests: (n: number, shown: string) => `${shown} ${n === 1 ? "request" : "requests"}`,
    window: "Period",
    days: (n: number) => (n === 1 ? "Last day" : `Last ${n} days`),
    list: "Network list",
    defaults: "Platform defaults only",
    sharesVm: "Shares the VM's network (VPN included)",
    loadFailed: "Could not load the network usage",
  },
  page: {
    blocked: "Blocked domains",
    blockedHint: "Distinct domains the agents tried and were refused, and how often.",
    noneBlocked: "Nothing was blocked in this period.",
    allowedDomains: (n: number) => `Allowed domains (${n})`,
    noneAllowed: "No requests in this period.",
    columns: { domain: "Domain", count: "Requests", lastSeen: "Last tried" },
    allow: "Allow",
    allowLabel: (domain: string) => `Allow ${domain}`,
    allowSelected: (n: number) => `Allow ${n} selected`,
    allowTitle: (n: number) => (n === 1 ? "Allow this domain?" : `Allow ${n} domains?`),
    allowBody: (list: string, groups: number) =>
      `This adds it to the list ${list}, used by ${groups} ${groups === 1 ? "task group" : "task groups"}. Running agents get it within seconds.`,
    allowed: (n: number) => `Allowed ${n} ${n === 1 ? "domain" : "domains"}`,
    noList: "This group uses the platform defaults only",
    noListBody: "To allow a domain, the group needs a network list of its own. Create one with the blocked domains, then choose it in the group's settings.",
    createWithBlocked: "Create a list with these domains",
    editGroup: "Change the group's list",
  },
  wizard: {
    list: "Network list",
    defaults: "Platform defaults only",
    listHint: "The domains the agents' containers may reach, on top of the platform essentials.",
    createList: "Create a list",
    share: "Share the VM's network (VPN included)",
    shareHint: "Lets the agents reach what your environment's VPN reaches: its domains and private addresses.",
  },
};
